import type { DayOfWeek } from '../../types/common';
import type { Lesson } from '../../types/timetable';

/**
 * Helper to generate lesson ID
 */
function generateLessonId(index: number): string {
  return `lesson-${String(index).padStart(4, '0')}`;
}

/**
 * Generate realistic lessons for a timetable
 * ~250 lessons covering Years 7-13 across 5 days, 5 periods
 */
export function generateMockLessons(timetableId: string): Lesson[] {
  const lessons: Lesson[] = [];
  let lessonIndex = 1;

  const days: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const periods = [1, 2, 3, 4, 5];

  // Common subjects for each year group
  const yearGroupSubjects = {
    'year-7': ['subject-eng', 'subject-math', 'subject-sci', 'subject-hist', 'subject-geog', 'subject-art', 'subject-pe'],
    'year-8': ['subject-eng', 'subject-math', 'subject-sci', 'subject-hist', 'subject-geog', 'subject-mus', 'subject-pe'],
    'year-9': ['subject-eng', 'subject-math', 'subject-sci', 'subject-hist', 'subject-geog', 'subject-dt', 'subject-pe'],
    'year-10': ['subject-eng', 'subject-math', 'subject-sci', 'subject-hist', 'subject-geog', 'subject-cs', 'subject-pe'],
    'year-11': ['subject-eng', 'subject-math', 'subject-sci', 'subject-hist', 'subject-geog', 'subject-bus', 'subject-pe'],
    'year-12': ['subject-eng-lit', 'subject-math-fm', 'subject-bio', 'subject-chem', 'subject-phys', 'subject-hist', 'subject-geog'],
    'year-13': ['subject-eng-lit', 'subject-math-fm', 'subject-bio', 'subject-chem', 'subject-phys', 'subject-econ', 'subject-pol'],
  };

  // Generate lessons for each year group
  for (const [yearGroup, subjects] of Object.entries(yearGroupSubjects)) {
    const classCount = yearGroup.startsWith('year-12') || yearGroup.startsWith('year-13') ? 2 : 6;

    for (let classNum = 0; classNum < classCount; classNum++) {
      const classId = `${yearGroup.replace('year-', 'class-')}${String.fromCharCode(97 + classNum)}`; // e.g., class-7a

      // Each class needs lessons for each subject
      for (const subjectId of subjects) {
        const lessonsPerWeek = subjectId.includes('eng') || subjectId.includes('math') ? 5 : subjectId.includes('sci') ? 3 : 2;

        for (let lessonNum = 0; lessonNum < lessonsPerWeek; lessonNum++) {
          const day = days[lessonNum % days.length];
          const period = periods[lessonNum % periods.length];

          // Select appropriate teacher and room
          const teacherId = `teacher-${(lessonIndex % 100) + 1}`;
          const roomId = subjectId.includes('sci')
            ? 'room-sci-lab-1'
            : subjectId.includes('art')
              ? 'room-art-1'
              : subjectId.includes('mus')
                ? 'room-music-1'
                : subjectId.includes('pe')
                  ? 'room-gym-main'
                  : subjectId.includes('dt')
                    ? 'room-dt-workshop'
                    : `room-${101 + (lessonIndex % 20)}`;

          lessons.push({
            id: generateLessonId(lessonIndex),
            timetableId,
            subjectId,
            teacherId,
            roomId,
            studentGroupType: 'class',
            studentGroupId: classId,
            studentCount: yearGroup.startsWith('year-12') || yearGroup.startsWith('year-13') ? 15 : 30,
            day,
            period,
            isDouble: false,
          });

          lessonIndex++;
        }
      }
    }
  }

  return lessons;
}

export const mockLessons = generateMockLessons('timetable-current');
